import * as utils from '../../../../common/general-utils';
import { stringify } from 'qs';
import promise from 'bluebird';
import SofyAbortController from '/src/common/sofy-abort-controller/sofy-abort-controller';

let listDatasetFilesAbortController;
let listJobHistoryAbortController;
let listDatasetHistoryFilesAbortController;

export const actions = {
  /**
   * Fetch dataset files list
   * @param { Object } ctx Vuex context
   * @param { String } UUID UUID of dataset
   */
  async listDatasetFiles({ commit }, { UUID }) {
    try {
      if (listDatasetFilesAbortController) {
        listDatasetFilesAbortController.abort();
      }
      listDatasetFilesAbortController = new SofyAbortController();

      commit('setReload', true);
      // Reset files as we are pushing batch of fetched files in loop
      commit('setDatasetFiles', { tablesCount: 0, files: [] });

      // Fetch and push files in batches uploadedFiles until we get all the files
      // When fetched continuationToken is null all the files are fetched
      let continuationToken = null;
      do {
        const result = await this.$axios.$get(
          `/api/datasetfiles/list/${UUID}?continuationToken=${JSON.stringify(continuationToken)}`,
          {
            signal: listDatasetFilesAbortController.signal
          }
        );

        commit('setDatasetFiles', { ...result, reset: false });
        continuationToken = result.continuationToken;
      } while (continuationToken);
    } catch (error) {
      if (error && error.message !== 'canceled') {
        this.$toast.error(this.$errorFromResponse('admin.dataset.details.errorDatasetFiles', error));
      }
    } finally {
      commit('setReload', false);
    }
  },

  /**
   * Clears job based on job type
   * @param { Object } ctx Vuex context
   * @param { String } jobStatus status of the job to be cleared
   * @param { String } UUID UUID of the dataset
   * @param { String[] } jobHistoryUUIDs job history records UUID
   */
  async clearImports({ dispatch }, { jobStatus, UUID, jobHistoryUUIDs, params }) {
    try {
      await this.$axios.$post('/api/job/dataset/clear/records', {
        jobId: 1002,
        status: jobStatus,
        datasetUUID: UUID,
        jobHistoryUUIDs
      });
      this.$toast.success(this.vueApp.$nuxt.$i18n.global.t('admin.dataset.details.deleted_record_success'));
      dispatch('fetchJobHistoryRecords', { params });
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.deleted_record_error', error));
    }
  },

  /**
   * Terminate ongoing import job
   * @param { Object } ctx Vuex context
   * @param { String } datasetUUID UUID of the dataset
   * @param { String } endpointServerUUID UUID of the dataset endpoint server
   * @param { number[] } jobHistoryUUIDs job history records UUID
   */
  async terminateImportJobs({ commit, dispatch }, { datasetUUID, jobHistoryUUIDs, importTabIndex }) {
    try {
      await this.$axios.$post(`/api/data/import/terminate/${datasetUUID}`, {
        jobHistoryUUIDs
      });
      this.$toast.success(this.vueApp.$nuxt.$i18n.global.t('admin.dataset.details.terminate_requested'));

      if (importTabIndex) {
        commit('setImportTabIndex', importTabIndex);
      }
      dispatch('listDatasetFiles', { UUID: datasetUUID });
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.terminate_error', error));
    }
  },

  /**
   * Retry job that failed
   * @param { Object } ctx Vuex context
   * @param { Array } jobs List of all failed jobs
   * @param { String } UUID UUID of the dataset
   */
  async retryFailedJobs({ commit, dispatch }, { jobs, UUID, params, type, importTabIndex }) {
    try {
      const result = await this.$axios.$put(`/api/data/import/retry?datasetUUID=${UUID}`, { jobs, type });
      this.$toast.success(result);
      if (params) dispatch('fetchJobHistoryRecords', { params });
      else dispatch('listDatasetFiles', { UUID });

      if (importTabIndex && result !== 'No files to Import') {
        commit('setImportTabIndex', importTabIndex);
      }
    } catch (error) {
      if (error && error['response'] && error['response']['data'] && error['response']['status'] === 409) {
        this.$toast.warn(this.vueApp.$nuxt.$i18n.global.t('backend.data_import.warning_in_retry'));
      } else {
        this.$toast.error(this.$errorFromResponse('admin.dataset.details.retrigger_failed_imports', error));
      }
    }
  },

  /**
   * Create imports for file
   * @param { Object } ctx Vuex context
   * @param { Array } files List of files for creating import
   * @param { String } UUID UUID of the dataset
   * @param { String } datasetTablePrefix table prefix of the dataset
   * @param { String } endpointUUID Primary endpoint UUID of the dataset
   * @param { String } datasetName Name of dataset
   */
  async createImportForFiles({ commit, dispatch }, { files, UUID, datasetTablePrefix, endpointUUID, importTabIndex }) {
    const filesInImport = files.map((file) => file.FileName);
    try {
      commit('setFilesInImport', filesInImport);
      const tables = files.map((file) => ({
        ...file,
        datasetTablePrefix,
        endpointUUID,
        datasetUUID: UUID,
        Selected: true
      }));
      const result = await this.$axios.$post('/api/data/import/create', {
        tables,
        datasetUUID: UUID
      });
      if (result.status === 'error') {
        this.$toast.error(result.message);
      } else if (result.status === 'warn') {
        this.$toast.warn(result.message);
      } else {
        this.$toast.success(result.message);
      }

      await dispatch('listDatasetFiles', { UUID });
      if (result.status === 'success' && importTabIndex) {
        commit('setImportTabIndex', importTabIndex);
      }
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.clear_imports', error));
    } finally {
      commit('resetFilesInImport', filesInImport);
    }
  },

  /**
   * Uploads file
   * @param { Object } ctx Vuex context
   * @param { String } postAction API for adding files in the list
   * @param { Array } files List of files for uploading
   * @param { String } UUID UUID of dataset
   * @param { String } datasetName Name of dataset
   */
  async uploadFiles({ commit, dispatch }, { files, datasetData, setUploadProgress, removeUploadProgress }) {
    try {
      const chunkSizeInBytes = 2e7;
      const timeStamp = new Date().getTime();
      const { Name: datasetName, UUID } = datasetData;
      await promise.map(
        files,
        async (file) => {
          if (file.uploading) return;
          else file.uploading = true;
          const maxLoadedPerChunk = {};
          await new Promise((resolve) => {
            utils.uploadFile(file.file, chunkSizeInBytes, async (formData, lastChunkCall, chunkId) => {
              if (!formData) return resolve();
              try {
                if (!maxLoadedPerChunk[chunkId]) {
                  maxLoadedPerChunk[chunkId] = 0;
                }
                await this.$axios.$post(
                  `/api/datasetfiles/${UUID}?${stringify({
                    datasetName: datasetName,
                    primaryEndpointUUID: datasetData.PrimaryEndpoint,
                    timeStamp: 'temp_' + timeStamp
                  })}`,
                  formData,
                  {
                    headers: {
                      'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (e) => {
                      if (e.loaded > maxLoadedPerChunk[chunkId]) {
                        maxLoadedPerChunk[chunkId] = e.loaded;
                        const chunkStartOffset = chunkId * chunkSizeInBytes;
                        const totalUploaded = chunkStartOffset + maxLoadedPerChunk[chunkId];
                        const progress = Math.min(Math.round((totalUploaded * 100) / file.size), 99);
                        setUploadProgress({ id: file.id, value: progress });
                      }
                    }
                  }
                );

                if (lastChunkCall) {
                  file.uploading = false;
                  files = files.filter((f) => f.uploading === true);
                  commit('setUploadFiles', files);

                  this.$toast.success(
                    this.vueApp.$nuxt.$i18n.global.t('admin.dataset.details.fileUpload.upload_success', {
                      fileName: file.name
                    })
                  );
                  dispatch('listDatasetFiles', { UUID });
                }
              } catch (error) {
                removeUploadProgress(file.id);
                this.$toast.error(`${this.vueApp.$nuxt.$i18n.global.t(
                  'admin.dataset.details.fileUpload.upload_file_error',
                  {
                    fileName: file.name
                  }
                )}.
                 Error: ${error.toString()}
                 `);
              } finally {
                resolve();
              }
            });
          });
        },
        {
          concurrency: 5
        }
      );
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.fileUpload.uploadFailed', error));
    }
  },

  /**
   * Downloads files using POST request
   * @param { Object } ctx Vuex context
   * @param { String[] } filesName Name of files for download
   * @param { Object } datasetDetail Details of Dataset
   */
  async downloadFiles(ctx, { filesName, datasetDetail }) {
    try {
      const result = await this.$axios.$post(
        `/api/datasetfiles/download/${datasetDetail.UUID}`,
        {
          fileName: filesName
        },
        {
          responseType: 'blob'
        }
      );

      const fileName =
        filesName.length === 1 ? filesName[0].substring(filesName[0].lastIndexOf('/') + 1) : 'Sofy_file_downloads.zip';
      const matches = fileName.match(/^(.*)(_\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2})(.*)$/);
      const originalFileName = matches ? `${matches[1]}${matches[3]}` : fileName;

      const url = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', originalFileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.download_error', error));
    }
  },

  /**
   * Deletes uploaded file
   * @param { Object } ctx Vuex context
   * @param { String } filesName Name of the files
   * @param { String } datasetName Name of the dataset
   * @param { String } UUID UUID of the dataset
   */
  async deleteUploadedFiles({ dispatch }, { filesName, UUID }) {
    try {
      await this.$axios.$post(`/api/datasetfiles/delete/${UUID}`, { fileName: filesName });
      this.$toast.success(this.vueApp.$nuxt.$i18n.global.t('admin.dataset.details.fileDeleteSuccess'));
      dispatch('listDatasetFiles', { UUID });
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.delete_upload_file', error));
    }
  },

  /**
   * Retrigger pending imports
   * @param { Object } ctx Vuex context
   * @param { String } datasetUUID UUID of the dataset
   */
  async retriggerPendingImports({ dispatch }, { datasetUUID, params }) {
    try {
      await this.$axios.$post(`/api/data/import/retrigger?datasetUUID=${datasetUUID}`);
      this.$toast.success(this.vueApp.$nuxt.$i18n.global.t('admin.dataset.details.retrigger_success'));
      if (params) dispatch('fetchJobHistoryRecords', { params });
      else dispatch('listDatasetFiles', { UUID: datasetUUID });
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.retrigger_error', error));
    }
  },

  async fetchJobHistoryRecords({ commit }, { params = {} }) {
    // To remove the deep references.
    if (listJobHistoryAbortController) {
      listJobHistoryAbortController.abort();
    }
    listJobHistoryAbortController = new SofyAbortController();

    const queryParams = JSON.parse(
      JSON.stringify({
        ...params,
        'orderBy[field]': params?.orderBy?.field,
        'orderBy[dir]': params?.orderBy?.dir,
        status: params.status,
        datasetUUID: params.datasetUUID
      })
    );
    delete queryParams.orderBy;
    try {
      commit('setReload', true);
      const records = await this.$axios.$get('/api/job/dataset/list/1002', {
        params: queryParams,
        signal: listJobHistoryAbortController.signal
      });
      commit('setJobHistoryRecords', { records });
      commit('setReload', false);
    } catch (error) {
      if (error?.message !== 'canceled') {
        this.$toast.error(this.$errorFromResponse('admin.dataset.details.record_fetch_error', error));
        commit('setReload', false);
      }
    }
  },

  /**
   * Fetch dataset files list
   * @param { Object } ctx Vuex context
   * @param { String } UUID UUID of dataset
   */
  async listDatasetHistoryFiles({ commit }, { UUID, params }) {
    try {
      if (listDatasetHistoryFilesAbortController) {
        listDatasetHistoryFilesAbortController.abort();
      }
      listDatasetHistoryFilesAbortController = new SofyAbortController();

      commit('setHistoryReload', true);

      // Reset files as we are pushing batch of fetched files in loop
      commit('setDatasetHistoryFiles', { records: [] });
      // Fetch and push files in batches uploadedFiles until we get all the files
      const result = await this.$axios.$get(`/api/datasetfiles/list/history/${UUID}`, {
        params,
        signal: listDatasetHistoryFilesAbortController.signal
      });
      commit('setDatasetHistoryFiles', { ...result, reset: false });
      commit('setHistoryReload', false);
    } catch (error) {
      if (error && error.message !== 'canceled') {
        this.$toast.error(this.$errorFromResponse('admin.dataset.details.errorDatasetHistoryFiles', error));
      }
    }
  },

  /**
   * Downloads public key
   * @param { Object } ctx Vuex context
   * @param { String } endpointUUID Endpoint UUID
   */
  async downloadPublicKey(ctx, endpointUUID) {
    try {
      const result = await this.$axios.$get(`/api/endpoint/get-public-key/${endpointUUID}`);
      const url = window.URL.createObjectURL(new Blob([result[0].PublicKey]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'Key.pub');
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('admin.dataset.details.download_error', error));
    }
  }
};
